import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CustomerType,
  CommercialCustomerKsvResponse,
  PrivateCustomerKsvResponse,
} from '../../../../../generated/ApiClient';
import { pushNotificationHandler } from '../../../../../common/PushNotification';
import { confirmHandler } from '../../../../../common/ConfirmModal';
import RestHttpClient from '../../../../../common/RestHttpClient';
import { usePermissions } from '../../../../../common/auth/PermissionsContext';
import { KsvSearchFormValues } from './useKsvSearchForm';
import { KsvSearchResult } from './useKsvSearch';
import { PrivateKsvSearchFields } from './PrivateKsvSearchFields';
import { CommercialKsvSearchFields } from './CommercialKsvSearchFields';
import { KsvSearchResultsTable } from './KsvSearchResultsTable';
import { useIdentityCheckSave } from '../hooks/useIdentityCheckSave';
import { parseRomanHousenumber } from '../../../../customers/ksv/KsvSearch.util';

interface LockedResult {
  ksvId: string;
  displayName: string;
  address: string;
}

interface Props {
  customerId: number;
  customerType: CustomerType;
  dealId: number;
  form: UseFormReturn<KsvSearchFormValues>;
  formLoading: boolean;
  search: () => Promise<KsvSearchResult[]>;
  results: KsvSearchResult[];
  isSearching: boolean;
  hasSearched: boolean;
  resetSearch: () => void;
  /** If the customer already has a stored KSV id, the section opens in saved-state. */
  initialKsvId?: string;
  /** True if the customer has KsvSearchDone=true but no KsvId — search closed without a hit. */
  initialNoHit?: boolean;
  onResultSelected: (ksvId: string) => void;
  onNoHitClosed: () => void;
  onReset: () => void;
}

export function KsvSearchSection({
  customerId,
  customerType,
  dealId,
  form,
  formLoading,
  search,
  results,
  isSearching,
  hasSearched,
  resetSearch,
  initialKsvId,
  initialNoHit,
  onResultSelected,
  onNoHitClosed,
  onReset,
}: Props) {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const canEditKsvRestrictedFields = hasPermission('KsvRestrictedFields', 'Edit');

  const { save, saving } = useIdentityCheckSave();
  const [lockedResult, setLockedResult] = useState<LockedResult | undefined>(undefined);
  const [saved, setSaved] = useState(false);
  const [noHitClosed, setNoHitClosed] = useState(!!initialNoHit);
  const [initialized, setInitialized] = useState(false);

  // Once the customer data is loaded into the form, hydrate the locked result
  // for an existing KsvId so the section opens in saved-state.
  useEffect(() => {
    if (initialized || formLoading) return;
    if (!initialKsvId) {
      setInitialized(true);
      return;
    }
    const v = form.getValues();
    let displayName: string;
    let address: string;
    if (customerType === CustomerType.Commercial) {
      displayName = v.companyName ?? '';
      address = `${v.zipCode ?? ''} ${v.city ?? ''}, ${v.street ?? ''} ${v.houseNumber ?? ''}`.trim();
    } else {
      displayName = `${v.firstName ?? ''} ${v.lastName ?? ''}`.trim();
      address = `${v.zipCode ?? ''} ${v.city ?? ''}, ${v.street ?? ''} ${v.houseNumber ?? ''}`.trim();
    }
    setLockedResult({ ksvId: initialKsvId, displayName, address });
    setSaved(true);
    setInitialized(true);
  }, [formLoading, initialKsvId, initialized, customerType, form]);

  const applyResult = (
    result: CommercialCustomerKsvResponse | PrivateCustomerKsvResponse,
    overwriteAddress: boolean,
  ) => {
    let displayName: string;

    if (customerType === CustomerType.Commercial) {
      const r = result as CommercialCustomerKsvResponse;
      form.setValue('companyName', r.companyName.trim(), { shouldDirty: true });
      if (overwriteAddress) {
        form.setValue('zipCode', r.zip.trim(), { shouldDirty: true });
        form.setValue('city', r.city.trim(), { shouldDirty: true });
        if (r.house) {
          form.setValue('street', r.street.trim(), { shouldDirty: true });
          form.setValue('houseNumber', r.house.trim(), { shouldDirty: true });
        } else {
          const { street, housenumber } = parseRomanHousenumber(r.street);
          form.setValue('street', street.trim(), { shouldDirty: true });
          form.setValue('houseNumber', housenumber.trim(), { shouldDirty: true });
        }
      }
      displayName = r.companyName;
    } else {
      const r = result as PrivateCustomerKsvResponse;
      form.setValue('firstName', r.firstName.trim(), { shouldDirty: true });
      form.setValue('lastName', r.lastName.trim(), { shouldDirty: true });
      form.setValue('birthDate', r.birthDate, { shouldDirty: true });
      if (overwriteAddress) {
        form.setValue('zipCode', r.zipCode.trim(), { shouldDirty: true });
        form.setValue('city', r.city.trim(), { shouldDirty: true });
        if (r.house) {
          form.setValue('street', r.street.trim(), { shouldDirty: true });
          form.setValue('houseNumber', r.house.trim(), { shouldDirty: true });
        } else {
          const { street, housenumber } = parseRomanHousenumber(r.street);
          form.setValue('street', street.trim(), { shouldDirty: true });
          form.setValue('houseNumber', housenumber.trim(), { shouldDirty: true });
        }
      }
      displayName = `${r.firstName} ${r.lastName}`;
    }

    const v = form.getValues();
    const address = `${v.zipCode ?? ''} ${v.city ?? ''}, ${v.street ?? ''} ${v.houseNumber ?? ''}`.trim();
    setLockedResult({ ksvId: result.ksvId, displayName, address });
  };

  const handleResultSelected = (ksvId: string) => {
    const result = results.find((r) => r.ksvId === ksvId);
    if (!result) return;

    confirmHandler.confirm(
      t('customers.ksv.result.acceptTitle'),
      () => applyResult(result, false),
      () => applyResult(result, true),
      t('customers.ksv.result.accept'),
      t('customers.ksv.result.noOverwriteMessage'),
      t('customers.ksv.result.overwriteMessage'),
    );
  };

  const handleSave = async () => {
    if (!lockedResult) return;
    const values = form.getValues();
    try {
      await save(customerId, {
        dealId,
        ksvId: lockedResult.ksvId,
        firstName: values.firstName,
        lastName: values.lastName,
        birthDate: values.birthDate,
        street: values.street,
        houseNumber: values.houseNumber,
        zipCode: values.zipCode,
        city: values.city,
        companyName: values.companyName,
        uidNumber: values.uidNumber,
        commercialRegisterNumber: values.commercialRegisterNumber,
      });
      setSaved(true);
      pushNotificationHandler.publish(t('identityCheck.ksv.saved'), 'success');
      onResultSelected(lockedResult.ksvId);
    } catch (error) {
      console.error('Failed to save identity check fields:', error);
    }
  };

  // Closes the search without picking a hit — sets KsvSearchDone=true on the
  // customer so the deal can be submitted (foreign customers, no KSV record).
  const handleCloseWithoutHit = async () => {
    const values = form.getValues();
    try {
      await save(customerId, {
        dealId,
        firstName: values.firstName,
        lastName: values.lastName,
        birthDate: values.birthDate,
        street: values.street,
        houseNumber: values.houseNumber,
        zipCode: values.zipCode,
        city: values.city,
        companyName: values.companyName,
        uidNumber: values.uidNumber,
        commercialRegisterNumber: values.commercialRegisterNumber,
      });
      setNoHitClosed(true);
      pushNotificationHandler.publish(t('identityCheck.ksv.noHitClosed'), 'warning');
      onNoHitClosed();
    } catch (error) {
      console.error('Failed to close KSV search without hit:', error);
    }
  };

  const handleReset = () => {
    setLockedResult(undefined);
    setSaved(false);
    setNoHitClosed(false);
    resetSearch();
    onReset();
    RestHttpClient.resetKsvSearch(customerId, dealId).catch((error) => {
      console.error('Failed to reset KSV search:', error);
    });
  };

  if (lockedResult) {
    return (
      <Box>
        <Paper
          variant="outlined"
          sx={{ p: 2, borderColor: 'success.main', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
        >
          <CheckCircleIcon color="success" sx={{ mt: 0.25, flexShrink: 0 }} />
          <Box>
            <Typography variant="subtitle2">{lockedResult.ksvId}</Typography>
            <Typography variant="body2">{lockedResult.displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {lockedResult.address}
            </Typography>
          </Box>
        </Paper>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="secondary" onClick={handleReset} disabled={saving}>
            {t('identityCheck.reset')}
          </Button>
          {!saved && (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {t('identityCheck.ksv.save')}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  if (formLoading || !initialized) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {noHitClosed && (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 2, borderColor: 'warning.main', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
        >
          <WarningAmberIcon color="warning" sx={{ mt: 0.25, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2">{t('identityCheck.ksv.noHitClosedInfo')}</Typography>
          </Box>
        </Paper>
      )}
      {customerType === CustomerType.Commercial
        ? <CommercialKsvSearchFields
            control={form.control}
            canEditAddress={canEditKsvRestrictedFields}
            canEditCompanyName={canEditKsvRestrictedFields}
            ksvSelected={false}
          />
        : <PrivateKsvSearchFields
            control={form.control}
            canEditAddress={canEditKsvRestrictedFields}
          />
      }
      <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
        <Button variant="contained" onClick={() => void search()} disabled={isSearching}>
          {isSearching ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          {t('identityCheck.ksv.search')}
        </Button>
        {hasSearched && results.length === 0 && !noHitClosed && (
          <Button
            variant="outlined"
            color="warning"
            onClick={handleCloseWithoutHit}
            disabled={saving}
          >
            {saving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            {t('identityCheck.ksv.closeWithoutHit')}
          </Button>
        )}
      </Box>
      <KsvSearchResultsTable
        results={results}
        customerType={customerType}
        onResultSelected={handleResultSelected}
      />
    </Box>
  );
}
